import { useCallback, useState } from 'react';

/**
 * Sets tabindex="-1" on an element if it doesn't already have one,
 * ensuring it can receive programmatic focus.
 */
const ensureFocusable = (el: HTMLElement): HTMLElement => {
  if (!el.hasAttribute('tabindex')) {
    el.setAttribute('tabindex', '-1');
  }
  return el;
};

export const useKeyboardNavigation = (_rowCount: number) => {
  const [focusedRowIndex, setFocusedRowIndex] = useState<number | null>(null);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    // Use e.target (the element that had focus when the key was pressed) rather than
    // document.activeElement. Child handlers (e.g. the Notes cell's own onKeyDown in
    // SCAL-8178) may call .focus() on another element before our handler runs, which
    // would cause document.activeElement to point to the wrong row and skip a row.
    const active = e.target as HTMLElement | null;
    if (!active) {
      return;
    }

    const currentCell =
      active.tagName === 'TD'
        ? (active as HTMLTableCellElement)
        : active.closest<HTMLTableCellElement>('td');
    const currentRow = active.tagName === 'TR' ? active : active.closest<HTMLElement>('tr');
    if (!currentRow) {
      return;
    }

    const tbody = currentRow.parentElement!;

    // Filter to rows that have tabindex explicitly set. This excludes the virtualizer's
    // height-padding <TableRow> spacers, which never get a tabIndex prop.
    const rows = Array.from(tbody.querySelectorAll<HTMLElement>(':scope > tr')).filter(r =>
      r.hasAttribute('tabindex')
    );
    const rowIdx = rows.indexOf(currentRow);
    const rowCells = Array.from(currentRow.querySelectorAll<HTMLElement>('td'));
    const colIdx = currentCell ? rowCells.indexOf(currentCell) : -1;

    // Focus a specific TD, preferring an InlineNoteEditor box inside it if present.
    const focusCellEl = (cell: HTMLElement) => {
      const noteEditor = cell.querySelector<HTMLElement>('[data-inline-note-editor="true"][tabindex="0"]');
      (noteEditor ?? ensureFocusable(cell)).focus();
    };

    const focusCell = (row: HTMLElement, idx: number) => {
      const cells = row.querySelectorAll<HTMLElement>('td');
      const target = cells[idx] ?? cells[cells.length - 1];
      if (target) {
        focusCellEl(target);
      }
    };

    // Move up (-1) or down (+1) within the same column.
    const navigateVertical = (delta: 1 | -1): boolean => {
      const targetRow = rows[rowIdx + delta];
      if (!targetRow) {
        return false;
      }
      if (colIdx >= 0) {
        focusCell(targetRow, colIdx);
      } else {
        ensureFocusable(targetRow).focus();
      }
      return true;
    };

    // Move left (-1) or right (+1) with wrap-around.
    const navigateHorizontal = (delta: 1 | -1): void => {
      if (colIdx < 0) {
        // Not on a cell: ArrowRight enters the first cell, ArrowLeft does nothing.
        const [first] = rowCells;
        if (delta === 1 && first) {
          focusCellEl(first);
        }
        return;
      }
      const nextIdx = (colIdx + delta + rowCells.length) % rowCells.length;
      const target = rowCells[nextIdx];
      if (!target) {
        return;
      }
      focusCellEl(target);
      // Scroll into view when wrapping past an edge.
      if (nextIdx !== colIdx + delta) {
        target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    };

    switch (e.key) {
      case 'ArrowDown':
        if (navigateVertical(1)) {
          e.preventDefault();
        }
        break;
      case 'ArrowUp':
        if (navigateVertical(-1)) {
          e.preventDefault();
        }
        break;
      case 'ArrowRight':
        navigateHorizontal(1);
        e.preventDefault();
        break;
      case 'ArrowLeft':
        navigateHorizontal(-1);
        e.preventDefault();
        break;
      case 'Enter': {
        // Notes cells handle Enter themselves — don't double-fire.
        if (active.dataset?.notesCell) {
          break;
        }
        const td = active.tagName === 'TD' ? active : active.closest<HTMLElement>('td');
        const activatable = td?.querySelector<HTMLElement>('[data-activatable]');
        (activatable ?? active).click();
        e.preventDefault();
        break;
      }
      default:
        break;
    }
  }, []);

  return { focusedRowIndex, setFocusedRowIndex, handleKeyDown };
};
